/**
 * LuizMelo Pet Dogs Pack (CC0) - https://luizmelo.itch.io/pet-dogs-pack
 * Sheets are padded to 100×100 cells (walk = 8 frames, idle = 10 frames).
 */

export type DogBreedId =
  | "golden"
  | "akita"
  | "dane"
  | "schnauzer"
  | "bernard"
  | "husky";

export interface DogBreedDef {
  id: DogBreedId;
  label: string;
  folder: string;
  /** Filename stem before -walk / -idle (case varies for Idle). */
  fileStem: string;
  idleFile: string;
  walkFile: string;
  /** Billboard size in world units (side-view sprites). */
  width: number;
  height: number;
}

/** Shared scale - chibi pups; world props use WORLD_PROP_SCALE to match. */
const S = 1.34;

export const DOG_BREEDS: readonly DogBreedDef[] = [
  {
    id: "golden",
    label: "Golden Retriever",
    folder: "Dog-1-Golden-Retriever",
    fileStem: "Golden-Retriever",
    idleFile: "Golden-Retriever-idle.png",
    walkFile: "Golden-Retriever-walk.png",
    width: 1.72 * S,
    height: 1.12 * S,
  },
  {
    id: "akita",
    label: "Akita",
    folder: "Dog-2-Akita",
    fileStem: "Akita",
    idleFile: "Akita-Idle.png",
    walkFile: "Akita-walk.png",
    width: 1.38 * S,
    height: 1.02 * S,
  },
  {
    id: "dane",
    label: "Great Dane",
    folder: "Dog-3-Great-Dane",
    fileStem: "Great-Dane",
    idleFile: "Great-Dane-idle.png",
    walkFile: "Great-Dane-walk.png",
    width: 1.95 * S,
    height: 1.38 * S,
  },
  {
    id: "schnauzer",
    label: "Schnauzer",
    folder: "Dog-4-Schnauzer",
    fileStem: "Schnauzer",
    idleFile: "Schnauzer-Idle.png",
    walkFile: "Schnauzer-walk.png",
    width: 1.48 * S,
    height: 1.12 * S,
  },
  {
    id: "bernard",
    label: "Saint Bernard",
    folder: "Dog-5-Saint-Bernard",
    fileStem: "Saint-Bernard",
    idleFile: "Saint-Bernard-Idle.png",
    walkFile: "Saint-Bernard-walk.png",
    width: 2.05 * S,
    height: 1.38 * S,
  },
  {
    id: "husky",
    label: "Siberian Husky",
    folder: "Dog-6-Siberian-Husky",
    fileStem: "Siberian-Husky",
    idleFile: "Siberian-Husky-Idle.png",
    walkFile: "Siberian-Husky-walk.png",
    width: 1.7 * S,
    height: 1.18 * S,
  },
] as const;

export const DOG_CELL = 100;
export const DOG_WALK_FRAMES = 8;
export const DOG_IDLE_FRAMES = 10;
export const DOG_PACK_ROOT = "/assets/luizmelo-pet-dogs";

/** Stable breed from fur color (players / ambient pups). */
export function breedFromFur(fur: number): DogBreedDef {
  const index = Math.abs(fur | 0) % DOG_BREEDS.length;
  return DOG_BREEDS[index]!;
}

/** NPC roles get a fixed recognizable breed. */
export function breedForNpc(kind: string | undefined): DogBreedDef {
  switch (kind) {
    case "banker":
      return DOG_BREEDS[4]!; // Saint Bernard
    case "barksmith":
      return DOG_BREEDS[2]!; // Great Dane
    case "shopkeeper":
      return DOG_BREEDS[0]!; // Golden
    case "guide":
      return DOG_BREEDS[5]!; // Husky
    case "vendor":
      return DOG_BREEDS[0]!; // Golden - friendly snack face
    default:
      return DOG_BREEDS[1]!; // Akita
  }
}
